import { useEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';

import { Text } from '@squire/ui';

import { GlobeIcon } from '@/components/_icons';
import type { LanguageBottomSheetRef } from '@/components/_shared/LanguageBottomSheet';
import { LanguageBottomSheet } from '@/components/_shared/LanguageBottomSheet';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { LanguageButtonProps } from './types';

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];

const LanguageButton = ({
  testID = 'screen-header-language-button',
}: LanguageButtonProps) => {
  const { t, i18n } = useScreenTranslations('shared');
  const sheetRef = useRef<LanguageBottomSheetRef>(null);
  const [hasOpened, setHasOpened] = useState(false);
  const languageKey = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(
    0,
    2,
  );
  const code = (
    SUPPORTED_LANGUAGES.includes(languageKey) ? languageKey : 'en'
  ).toUpperCase();

  useEffect(() => {
    if (hasOpened) sheetRef.current?.open();
  }, [hasOpened]);

  const handlePress = () => {
    if (hasOpened) {
      sheetRef.current?.open();
      return;
    }
    setHasOpened(true);
  };

  return (
    <>
      <Pressable
        onPress={handlePress}
        hitSlop={10}
        style={styles.languageButton}
        accessibilityRole="button"
        accessibilityLabel={t('screenHeader.languageButton.accessibilityLabel')}
        accessibilityHint={t('screenHeader.languageButton.accessibilityHint')}
        testID={testID}
      >
        <GlobeIcon size={18} color="iconSecondary" />
        <Text variant="sStrong" color="textSecondary">
          {code}
        </Text>
      </Pressable>
      {hasOpened && (
        <LanguageBottomSheet ref={sheetRef} testID={`${testID}-sheet`} />
      )}
    </>
  );
};

LanguageButton.displayName = 'LanguageButton';
export { LanguageButton };
